import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type ToastTone = 'success' | 'error' | 'info'

export interface ToastItem {
  id: number
  message: string
  tone: ToastTone
  durationMs: number
  createdAt: number
}

let seq = 1

export const useToastStore = defineStore('toast', () => {
  const items = ref<ToastItem[]>([])
  const timers = new Map<number, ReturnType<typeof setTimeout>>()

  const list = computed(() => items.value)

  function dismiss(id: number) {
    const t = timers.get(id)
    if (t) {
      clearTimeout(t)
      timers.delete(id)
    }
    items.value = items.value.filter((x) => x.id !== id)
  }

  function push(message: string, tone: ToastTone = 'info', durationMs = 3200) {
    const msg = String(message || '').trim()
    if (!msg) return 0
    const id = seq++
    const item: ToastItem = {
      id,
      message: msg,
      tone,
      durationMs: Math.max(1200, durationMs),
      createdAt: Date.now(),
    }
    items.value = [...items.value, item].slice(-4)
    const timer = setTimeout(() => dismiss(id), item.durationMs)
    timers.set(id, timer)
    return id
  }

  function success(message: string, durationMs?: number) {
    return push(message, 'success', durationMs)
  }
  function error(message: string, durationMs?: number) {
    return push(message, 'error', durationMs ?? 4000)
  }
  function info(message: string, durationMs?: number) {
    return push(message, 'info', durationMs)
  }

  return { list, items, push, success, error, info, dismiss }
})
